import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, cn } from "@guardian/shared-ui";
import { getCanonicalDomain } from "@guardian/shared-utils";
import { LanguageSwitcher } from "./language-switcher";
import { ThemeSwitcher } from "./theme-switcher";
import { isAllowedNavigation } from "@/utils/urlValidation";
import { useBranding } from "@/contexts/BrandingProvider";
import { ContactDetailsPopup } from "@/components/branding/ContactDetailsPopup";

type NavItem =
  | { kind: "button"; key: string; label: string; onClick: () => void }
  | { kind: "external"; key: string; label: string; href: string };

export const MainHeader: React.FC = () => {
  const { t } = useTranslation();
  const homeUrl = import.meta.env.VITE_HOME_URL;
  const { branding, isBranded } = useBranding();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [contactPopupOpen, setContactPopupOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleExternalNav = (href: string) => {
    if (isAllowedNavigation(href)) {
      window.location.href = href;
    }
    setIsMenuOpen(false);
  };

  // WHY useMemo: navItems is derived from branding state and translation funcs;
  // recomputing every render is cheap but stable refs keep the AnimatePresence
  // child keys deterministic.
  //
  // WHY branded About + Contact-LINK render as plain <a target="_blank"> instead
  // of going through isAllowedNavigation: those URLs are Owner-curated brand URLs
  // (e.g. https://vincar.com/about) that the backend already validates at save
  // time via OwnerBrandingManager.validateHttpsUrl — https-only, length-bound,
  // scheme-restricted. The frontend allowlist (origins from VITE_HOME_URL /
  // VITE_API_BASE_URL) was designed for the non-branded marketing-site path and
  // silently rejects every legitimate Owner URL. Mirror the FloatingWhatsAppButton
  // pattern (anchor + rel="noopener noreferrer") to keep tab-isolation guarantees.
  const navItems: NavItem[] = useMemo(() => {
    if (isBranded && branding) {
      const items: NavItem[] = [];
      if (branding.aboutUrl) {
        items.push({
          kind: "external",
          key: "about",
          label: t("nav.about"),
          href: branding.aboutUrl,
        });
      }
      if (branding.contactMode === "LINK" && branding.contactUrl) {
        items.push({
          kind: "external",
          key: "contact",
          label: t("nav.contact"),
          href: branding.contactUrl,
        });
      } else if (branding.contactMode === "DETAILS") {
        items.push({
          kind: "button",
          key: "contact",
          label: t("nav.contact"),
          onClick: () => {
            setContactPopupOpen(true);
            setIsMenuOpen(false);
          },
        });
      }
      return items;
    }
    return [
      {
        kind: "button",
        key: "about",
        label: t("nav.about"),
        onClick: () => handleExternalNav(`${homeUrl}/about`),
      },
      {
        kind: "button",
        key: "contact",
        label: t("nav.contact"),
        onClick: () => handleExternalNav(`${homeUrl}/contact`),
      },
    ];
  }, [isBranded, branding, homeUrl, t]);

  const brandedHomeHref = useMemo(() => {
    if (isBranded && branding) {
      return `https://${branding.slug}.${getCanonicalDomain()}/`;
    }
    return "/";
  }, [isBranded, branding]);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full border-b backdrop-blur-md transition-all duration-300 safe-top",
        isScrolled ? "bg-background/80" : "bg-background"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="flex h-20 items-center justify-between">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-2"
          >
            {isBranded && branding ? (
              <a href={brandedHomeHref} className="flex items-center space-x-2 min-w-0" data-testid="branded-home-link">
                <img
                  src={branding.logoUrl}
                  alt={branding.displayName}
                  className="h-12 w-auto max-w-[180px] object-contain sm:max-w-xs"
                />
                <span className="font-poppins-medium text-base hidden sm:inline-block">
                  {branding.displayName}
                </span>
              </a>
            ) : (
              <a href="/" className="flex items-center space-x-2">
                <img
                  src="/Logo.svg"
                  alt="Guardian Logo"
                  className="h-12 w-auto dark:hidden"
                />
                <img
                  src="/Logo2.svg"
                  alt="Guardian Logo"
                  className="h-12 w-auto hidden dark:block"
                />
              </a>
            )}
          </motion.div>

          <div className="flex items-center gap-4">
            <motion.nav
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="hidden md:flex items-center gap-6"
            >
              {navItems.map((item) =>
                item.kind === "external" ? (
                  <a
                    key={item.key}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`nav-${item.key}`}
                    className="inline-flex items-center justify-center rounded-md px-3 h-9 font-poppins-medium text-base hover:text-primary hover:bg-accent transition-colors"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Button
                    key={item.key}
                    variant="ghost"
                    size="sm"
                    className="font-poppins-medium text-base hover:text-primary"
                    onClick={item.onClick}
                    data-testid={`nav-${item.key}`}
                  >
                    {item.label}
                  </Button>
                ),
              )}
            </motion.nav>

            <LanguageSwitcher />
            <ThemeSwitcher />

            <div className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMenuOpen((prev) => !prev)}
                aria-label="Toggle Mobile Menu"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {isMenuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden border-t bg-background"
          >
            <div className="container px-4 py-4">
              <nav className="flex flex-col space-y-4 mb-6">
                {navItems.map((item) =>
                  item.kind === "external" ? (
                    <motion.a
                      key={item.key}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`nav-${item.key}-mobile`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2 }}
                      className="text-left font-poppins-medium text-lg text-muted-foreground hover:text-foreground"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.label}
                    </motion.a>
                  ) : (
                    <motion.button
                      key={item.key}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2 }}
                      className="text-left font-poppins-medium text-lg text-muted-foreground hover:text-foreground"
                      onClick={item.onClick}
                    >
                      {item.label}
                    </motion.button>
                  ),
                )}
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {isBranded && branding?.contactMode === "DETAILS" && (
        <ContactDetailsPopup
          open={contactPopupOpen}
          onOpenChange={setContactPopupOpen}
          email={branding.contactEmail}
          phone={branding.contactPhone}
          whatsapp={branding.contactWhatsapp}
          address={branding.contactAddress}
        />
      )}
    </header>
  );
};
